import { FreeAccountSignup } from "@ecologi/react-components/src/FreeAccountSignup";
import { FreeSignupFormValues } from "@ecologi/react-components/src/FreeAccountSignup/FreeSignupForm";
import { hasQueryInitiated, User } from "@ecologi/shared";
import { useRouter } from "next/router";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { useRecaptcha } from "@ecologi/react-components";
import { Head } from "../../../components";
import { APIError, formatAPIErrorForView } from "../../../lib";
import {
  HubspotField,
  hubspotPayAsYouGoForm,
} from "../../../lib/api/hubspot-pay-as-you-go-form";
import { createFreeAccount } from "../../../lib/api/subscriptions";
import { useStore } from "../../../store";
import { CAPTCHA_ACTIONS } from "@ecologi/shared";
import { fetchFromAPI } from "../../../lib/api/fetch-from-api";

const customPropertyNames = {
  lastName: "lastname",
  firstName: "firstname",
  groupName: "company",
} as const;

const notIncludingProperties = ["password"];

export function FreePlansPage() {
  const router = useRouter();
  const recaptcha = useRecaptcha();
  const { user, setUser, isCheckingUserSession, location, referrerUserId } =
    useStore();

  //  This route will also have an optional 'parent' query parameter which we need to send to the
  //  registration handler to indicate the profile being created is for a sub-forest. If no
  //  such parameter is supplied then the profile will be a standalone profile
  const { parent } = router.query;

  const parentUsername = Array.isArray(parent) ? parent[0] : parent;

  const { data: parentExists } = useQuery(
    ["parentProfile", parentUsername],
    async () => {
      try {
        await fetchFromAPI(`/users/${parentUsername}/profile`);
        return true;
      } catch (error) {
        if (error instanceof APIError && APIError.isNotFoundError(error)) {
          return false;
        }
        return null;
      }
    },
    { enabled: !!parentUsername }
  );

  const hubspotPayAsYouGoBodyFormatter = (
    formValues: FreeSignupFormValues
  ): HubspotField[] => {
    return Object.keys(formValues).reduce(
      (acc: HubspotField[], key: string) => {
        if (notIncludingProperties.includes(key)) return acc;
        acc.push({
          name: customPropertyNames[key] ?? key,
          value: formValues[key],
        });
        return acc;
      },
      []
    );
  };

  const [createAccountMutation, createAccountResult] = useMutation(
    async (data: FreeSignupFormValues) => {
      const token = await recaptcha.execute(CAPTCHA_ACTIONS.PAYG);
      const user = await createFreeAccount({
        type: "pay-as-you-go",
        captchaToken: token,
        customer: {
          emailAddress: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          location: data.country,
        },
        user: {
          affiliateClickId: undefined,
          groupName: data.groupName,
          isMarketingAccepted: false,
          parent: parentUsername,
          password: data.password,
          referralSource: undefined,
          referrerUserId,
        },
      });

      hubspotPayAsYouGoForm({
        fields: hubspotPayAsYouGoBodyFormatter(data),
      });

      return user;
    },
    {
      onError: (error: APIError) => {
        toast.error(formatAPIErrorForView(error));
      },
      onSuccess: ({ user }) => {
        setUser(new User(user));
        router.push("/thanks");
      },
    }
  );

  const hasSignupInitiated = hasQueryInitiated(createAccountResult);

  // Passing 'isLoading' as true even when the query has succeeded ensures the form
  // doesn't unlock in the brief moment between the mutation succeeding and the user
  // being sent to the thanks page
  const isLoading =
    createAccountResult.isLoading || createAccountResult.isSuccess;

  return (
    <>
      <Head title="Create a free account" />
      <FreeAccountSignup
        hasSignupInitiated={hasSignupInitiated}
        isCheckingUserSession={isCheckingUserSession}
        user={user}
        parentUsername={parentUsername}
        parentExists={parentExists ?? null}
      >
        <FreeAccountSignup.Form
          createFreeAccount={createAccountMutation}
          location={location}
          isLoading={isLoading}
        />
      </FreeAccountSignup>
    </>
  );
}
